import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { PerspectiveResult, Question } from "~vue/pages/perspectiveTypes";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed } from "vue";

export type AssessmentNotStarted = {
    status: "not_started";
    questions: Question[];
};

export type AssessmentInProgress = {
    status: "in_progress";
    session_id: string; // UUID string from the API
    questions: Question[];
    saved_responses: Record<string, number>;
};

export type AssessmentCompleted = {
    status: "completed";
    result: PerspectiveResult;
    questions: Question[];
};

export type AssessmentState = AssessmentNotStarted | AssessmentInProgress | AssessmentCompleted;

export const usePerspective = ({ source }: { source: string }) => {
    const {
        data: assessmentStatus,
        error: assessmentError,
        execute: executeAssessmentFetch,
        isFetching: isFetchingAssessment,
    } = useFetch("/v1/perspectives/assessment", { immediate: false }).get().json<AssessmentState>();

    const handleFetchAssessment = async () => {
        assessmentError.value = "";
        await executeAssessmentFetch();
    };

    const {
        error: startError,
        execute: executeStartAssessment,
        isFetching: isStartingAssessment,
        onFetchResponse: onStartResponse,
    } = useFetch("/v1/perspectives/sessions", { immediate: false }).post({}).json();

    onStartResponse(async () => {
        await logUserInteraction(AmplitudeEventNames.PERSPECTIVE_ASSESSMENT_STARTED, { assessment_type: "perspective", source });
        router.visit("/perspective/");
    });

    const startAssessmentErrorMessage = computed(() => (startError.value ? "Could not start your assessment. Please try again." : null));

    async function handleStartAssessment() {
        // If a session is already in progress (e.g. a previous retake the user navigated away from
        // without continuing or cancelling), resume it instead of POSTing a new one — creating a
        // second in-progress session returns a 409. Navigating to /perspective/ routes to the right
        // screen (the results retake banner, or the questions if there's no completed result yet).
        if (assessmentStatus.value?.status === "in_progress") {
            router.visit("/perspective/");
            return;
        }
        startError.value = "";
        await executeStartAssessment();
    }

    function handleContinueAssessment() {
        router.visit("/perspective/questions/");
    }

    return {
        assessmentStatus,
        assessmentError,
        handleFetchAssessment,
        isFetchingAssessment,
        startError,
        executeStartAssessment,
        isStartingAssessment,
        startAssessmentErrorMessage,
        handleStartAssessment,
        handleContinueAssessment,
    };
};
